import { createSelector } from "@reduxjs/toolkit";
import type { RoomSectionRenderItem } from "@multiconsult-group/multiplacer-ui";
import type { IRootState } from "../app/store";
import type { MlHeatmapTile } from "../DesignTab/designSlice";
import { selectHttpMcbimSpacesResult } from "../McBim/httpMcBimSelectors";
import { createDeviceCeilingHeightResolver, type CeilingHeightSpace } from "./deviceCeilingHeight";

export const ML_HEATMAP_3D_ID_PREFIX = "ml-heatmap:";
const HEAT_TILE_DEPTH_M = 0.2;

export const buildMlHeatmap3dItems = (
  tiles: MlHeatmapTile[],
  roomNumber: string | null | undefined,
  spaces: CeilingHeightSpace[]
): RoomSectionRenderItem[] => {
  if (!roomNumber || !tiles.length) return [];
  const getHeight = createDeviceCeilingHeightResolver(
    spaces.filter((space) => space.name === roomNumber)
  );
  return tiles.flatMap((tile, index) => {
    if (
      tile.color[3] <= 0 ||
      tile.points.length < 4 ||
      !tile.points.every((p) => p.length >= 2 && Number.isFinite(p[0]) && Number.isFinite(p[1]))
    )
      return [];
    const [a, b, , d] = tile.points;
    const x = (b[0] + d[0]) / 2,
      y = (b[1] + d[1]) / 2;
    const height = getHeight({ coordinate: [x, y], roomNumber });
    const length = Math.hypot(b[0] - a[0], b[1] - a[1]);
    const width = Math.hypot(d[0] - a[0], d[1] - a[1]);
    if (height == null || !Number.isFinite(height) || length <= 0 || width <= 0) return [];
    return [
      {
        item: {
          _id: `${ML_HEATMAP_3D_ID_PREFIX}${roomNumber}:${index}`,
          geometry: {
            Obb3DWorldMmList: [
              {
                centerM: { x, y, z: height + HEAT_TILE_DEPTH_M / 2 },
                axisA: { x: (b[0] - a[0]) / length, y: (b[1] - a[1]) / length, z: 0 },
                axisB: { x: (d[0] - a[0]) / width, y: (d[1] - a[1]) / width, z: 0 },
                axisC: { x: 0, y: 0, z: 1 },
                extentAM: length / 2,
                extentBM: width / 2,
                extentCM: HEAT_TILE_DEPTH_M / 2,
              },
            ],
          },
        },
        color: `#${tile.color
          .slice(0, 3)
          .map((value) => value.toString(16).padStart(2, "0"))
          .join("")}`,
        opacity: tile.color[3] / 255,
      },
    ];
  });
};

export const selectMlHeatmap3dItems = createSelector(
  [
    (state: IRootState) => state.design.mlHeatmapTiles,
    (state: IRootState) => state.design.selectedSpaceNumber,
    selectHttpMcbimSpacesResult,
  ],
  buildMlHeatmap3dItems
);
